import { useState, FormEvent } from 'react';
import { PaymentElement, useStripe, useElements } from '@stripe/react-stripe-js';
import type { SubscriptionTier } from '../pages/subscription/constants';
import styles from './StripePaymentModal.module.css';

interface StripePaymentFormProps {
  onCancel: () => void;
  onSuccess: () => void;
  tier: SubscriptionTier;
}

export function StripePaymentForm({ onCancel, onSuccess, tier }: StripePaymentFormProps) {
  const stripe = useStripe();
  const elements = useElements();
  const [error, setError] = useState<string | null>(null);
  const [isProcessing, setIsProcessing] = useState(false);

  const handleSubmit = async (e: FormEvent) => {
    e.preventDefault();

    if (!stripe || !elements) {
      setError('Payment system not ready. Please try again.');
      return;
    }

    setIsProcessing(true);
    setError(null);

    try {
      const { error: submitError } = await stripe.confirmPayment({
        elements,
        confirmParams: {
          return_url: window.location.origin + '/subscription',
        },
        redirect: 'if_required',
      });

      if (submitError) {
        setError(submitError.message || 'Payment failed. Please try again.');
        setIsProcessing(false);
      } else {
        // Payment succeeded
        onSuccess();
      }
    } catch (err) {
      setError('An unexpected error occurred. Please try again.');
      setIsProcessing(false);
    }
  };

  const handleBackdropClick = (e: React.MouseEvent<HTMLDivElement>) => {
    // Only close if clicking the backdrop, not the modal content
    if (e.target === e.currentTarget) {
      onCancel();
    }
  };

  return (
    <div className={styles.backdrop} onClick={handleBackdropClick}>
      <div className={styles.container}>
        <div className={styles.header}>
          <h2 className={styles.title}>Complete Your Subscription to {tier}</h2>
        </div>

        <form onSubmit={handleSubmit} className={styles.form}>
          <div className={styles.paymentElement}>
            <PaymentElement
              options={{
                layout: {
                  type: 'accordion',
                  defaultCollapsed: false,
                  radios: false,
                  spacedAccordionItems: true
                }
              }}
            />
          </div>

          {error && (
            <div className={styles.error}>
              {error}
            </div>
          )}

          {!stripe && (
            <div className={styles.error}>
              Loading payment system...
            </div>
          )}

          <div className={styles.actions}>
            <button
              type="button"
              onClick={onCancel}
              disabled={isProcessing}
              className={`${styles.button} ${styles.buttonSecondary}`}
            >
              Cancel
            </button>
            <button
              type="submit"
              disabled={isProcessing || !stripe || !elements}
              className={`${styles.button} ${styles.buttonPrimary}`}
            >
              {isProcessing ? 'Processing...' : 'Subscribe Now'}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}